import updateMailTipsForRecipients from '../actions/updateMailTipsForRecipients';
import { ComposeViewState, ComposeOperation, GroupComposeViewState } from 'owa-mail-compose-store';
import getCurrentComposeViewState from '../utils/findComposeFromRecipientWell';
import getRelativeComplementOfComposeRecipients from '../utils/getRelativeComplementOfComposeRecipients';
import getRecipientWellValues from '../utils/getRecipientWellValues';
import { getSharingTipRecipientInfoFromComposeViewState } from '../utils/getSharingTipRecipientInfoFromComposeViewState';
import isSmartComposeEnabled from '../utils/isSmartComposeEnabled';
import { refreshSharingTips } from '../utils/refreshSharingTips';
import { lazyTriggerRecipientsChangedEvent, RecipientFieldEnum } from 'owa-addins-core';
import { logUsage } from 'owa-analytics';
import { isCloudyAttachmentType } from 'owa-attachment-model-store/lib/utils/isCloudyAttachment';
import { updateRecipientsForSmartCompose } from 'owa-editor-smartcompose-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import triggerPolicyTips from 'owa-mail-compose-actions/lib/actions/triggerPolicyTips';
import trySaveMessage from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import { getCurrentTableMailboxInfo } from 'owa-mail-mailboxinfo';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import addReadWriteRecipients from 'owa-readwrite-recipient-well/lib/actions/addReadWriteRecipients';
import onRecipientsChanged from 'owa-readwrite-recipient-well/lib/actions/onRecipientsChanged';
import removeRecipientFromRecipientWell from 'owa-recipient-common/lib/actions/removeRecipientFromRecipientWell';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import ReadWriteRecipientWellViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientWellViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import getReadWriteRecipientViewState from 'owa-recipient-common/lib/utils/getReadWriteRecipientViewState';
import { lazyTryCheckPermForLinks } from 'owa-recipient-permission-checker';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { SharingTipRecipientInfo } from 'owa-sharing-data';
import { orchestrator } from 'satcheljs';
import {
    infoBarAddHandler,
    infoBarRemoveHandler,
    infoBarGetIdsHandler,
} from '../utils/infoBarHandlers';
import {
    includeMostRecentAttachmentsInfoBarMessage,
    INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID,
    RecipientsCollection,
} from 'owa-attachment-well-data/lib/utils/includeMostRecentAttachmentsInfoBarMessage';
import showSubjectPickerButton from '../actions/showSubjectPickerButton';
import { isSubjectPickerSupported } from 'owa-mail-subject-picker-connector/lib/utils/isSubjectPickerSupported';
import isThereOnlyOneToRecipient from '../utils/isThereOnlyOneToRecipient';
import { oneRecipientSelectedAction } from 'owa-readwrite-recipient-well';

const NO_RECIPIENTS_MESSAGEID = 'errorMessageNoRecipentsMailCanNotBeSend';

// Exported for testing purposes only
export const composeIdsWithActedOnMostRecentAttachmentMessage: string[] = [];

export function removeComposeIdFromActedOnMostRecentAttachmentList(composeId: string) {
    const index = composeIdsWithActedOnMostRecentAttachmentMessage.indexOf(composeId);
    if (index !== -1) {
        composeIdsWithActedOnMostRecentAttachmentMessage.splice(index, 1);
    }
}

orchestrator(addReadWriteRecipients, actionMessage => {
    const { recipientData, recipientWell } = actionMessage;
    const recipients: ReadWriteRecipientViewState[] = recipientData.map(entry =>
        getReadWriteRecipientViewState(entry.newPersona, entry.isValid, entry.newEmailAddress)
    );

    if (isPolicyTipsEnabled()) {
        updatePolicyTips(recipientWell);
    }

    updateMailtips(recipientWell, recipients);
});

orchestrator(removeRecipientFromRecipientWell, actionMessage => {
    const { recipientWellRecipients } = actionMessage;

    updateInfoBarMessages(recipientWellRecipients);

    if (isPolicyTipsEnabled()) {
        updatePolicyTips(recipientWellRecipients);
    }
});

function getRecipientType(
    viewState: ComposeViewState,
    recipientWellViewState: ReadWriteRecipientWellViewState
): RecipientFieldEnum {
    if (recipientWellViewState === viewState.toRecipientWell) {
        return RecipientFieldEnum.To;
    } else if (recipientWellViewState === viewState.ccRecipientWell) {
        return RecipientFieldEnum.Cc;
    } else if (recipientWellViewState === viewState.bccRecipientWell) {
        return RecipientFieldEnum.Bcc;
    }

    return null;
}

orchestrator(onRecipientsChanged, async actionMessage => {
    const { recipientWellRecipients, recipientData } = actionMessage;
    if (recipientData) {
        const recipients: ReadWriteRecipientViewState[] = recipientData.map(entry =>
            getReadWriteRecipientViewState(entry.newPersona, entry.isValid, entry.newEmailAddress)
        );

        recipients.forEach(newRecipient => {
            updateInfoBarMessages(recipientWellRecipients, newRecipient.displayText);
        });
    }

    if (isFeatureEnabled('rp-addins')) {
        const viewState = getCurrentComposeViewState(recipientWellRecipients);
        if (viewState) {
            const type = getRecipientType(viewState, recipientWellRecipients);
            lazyTriggerRecipientsChangedEvent
                .import()
                .then(triggerRecipientsChangedEvent =>
                    triggerRecipientsChangedEvent(viewState.composeId, type)
                );
            const recipientInfos: SharingTipRecipientInfo[] = await getSharingTipRecipientInfoFromComposeViewState(
                viewState
            );
            lazyTryCheckPermForLinks.importAndExecute(
                viewState.attachmentWell.sharingLinkIds,
                recipientInfos,
                viewState.composeId
            );
            refreshSharingTips(viewState);
        }
    }

    if (isSmartComposeEnabled()) {
        updateRecipientsForSmartCompose.importAndExecute(recipientWellRecipients.recipients);
    }

    showOrHideSubjectPickerButton(recipientWellRecipients);
});

function updateMailtips(
    recipientWell: RecipientWellWithFindControlViewState,
    recipients: ReadWriteRecipientViewState[]
) {
    const composeViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!composeViewState) {
        return;
    }

    updateMailTipsForRecipients(composeViewState.composeId, recipientWell, recipients);
}

function updatePolicyTips(recipientWell: ReadWriteRecipientWellViewState) {
    const currentViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!currentViewState) {
        return;
    }

    triggerPolicyTips(currentViewState, EventTrigger.RecipientWell);
}

export async function updateInfoBarMessages(
    recipientWell: ReadWriteRecipientWellViewState,
    newRecipientName?: string
) {
    const currentViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!currentViewState) {
        return;
    }

    //If in groups, we may not have a saved draft message yet. Save it before going ahead to avoid attachment creation failure.
    if (!currentViewState.itemId && (currentViewState as GroupComposeViewState).groupId) {
        await trySaveMessage(currentViewState);
    }

    const recipients: RecipientsCollection = getRecipientWellValues(currentViewState);
    if (newRecipientName) {
        removeNoRecipientsError(currentViewState);
        updateIncludeMostRecentAttachmentsMessage(currentViewState, recipients, newRecipientName);
    } else {
        updateIncludeMostRecentAttachmentsMessage(currentViewState, recipients);
    }
}

function showOrHideSubjectPickerButton(recipientWell: ReadWriteRecipientWellViewState) {
    // Ideally we shouldn't need this here but that would require a large refactor which is planned later on. WI: 51805 will track this work.
    if (isSubjectPickerSupported()) {
        const viewState = getCurrentComposeViewState(recipientWell);
        if (viewState) {
            if (isThereOnlyOneToRecipient(viewState)) {
                oneRecipientSelectedAction(recipientWell);
            } else {
                showSubjectPickerButton(viewState, false);
            }
        }
    }
}

function removeNoRecipientsError(viewState: ComposeViewState) {
    // VSO 28792: When adding a recipient, remove the no recipients error message if this exists
    infoBarRemoveHandler(viewState)([NO_RECIPIENTS_MESSAGEID]);
}

// Exported for testing purposes only
export function updateIncludeMostRecentAttachmentsMessage(
    viewState: ComposeViewState,
    recipients: RecipientsCollection,
    newRecipientName?: string
) {
    if (viewState.referenceItemId) {
        const item = getStore().items.get(viewState.referenceItemId.Id);
        if (item && !hasUserActedOnIncludeMostRecentAttachmentsMessage(viewState.composeId)) {
            const addedRecipientsInCompose: string[] = getRelativeComplementOfComposeRecipients(
                recipients,
                item
            );
            const numberOfAddedRecipientsInCompose: number = addedRecipientsInCompose.length;
            const hasValidAttachments =
                item.HasAttachments &&
                item.Attachments.filter(attachment =>
                    filterAttachmentsBasedOnAttachmentPolicy(attachment)
                ).length > 0;
            if (
                shouldIncludeMostRecentAttachmentsInfoBarMessage(
                    viewState,
                    hasValidAttachments,
                    numberOfAddedRecipientsInCompose
                )
            ) {
                logShowIncludeMostRecentAttachmentsMessageDatapoint(viewState);

                includeMostRecentAttachmentsInfoBarMessage(
                    { ...viewState.itemId, mailboxInfo: getCurrentTableMailboxInfo() },
                    item,
                    viewState.attachmentWell,
                    numberOfAddedRecipientsInCompose === 1
                        ? addedRecipientsInCompose[0]
                        : newRecipientName,
                    numberOfAddedRecipientsInCompose,
                    infoBarAddHandler(viewState),
                    infoBarRemoveHandler(viewState),
                    updateActedComposeHandler(viewState.composeId),
                    filterAttachmentsBasedOnAttachmentPolicy
                );
            } else if (
                shouldRemoveMostRecentAttachmentsInfoBarMessage(
                    viewState,
                    numberOfAddedRecipientsInCompose
                )
            ) {
                infoBarRemoveHandler(viewState)([INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID]);
            }
        }
    }
}

export function filterAttachmentsBasedOnAttachmentPolicy(attachment): boolean {
    const userConfig = getUserConfiguration();
    if (isCloudyAttachmentType(attachment)) {
        return userConfig.AttachmentPolicy.ReferenceAttachmentsEnabled;
    } else {
        return userConfig.AttachmentPolicy.ClassicAttachmentsEnabled;
    }
}

// Exported for testing purposes only
export function shouldIncludeMostRecentAttachmentsInfoBarMessage(
    viewState: ComposeViewState,
    parentMessageHasAttachments: boolean,
    numberOfDistinctRecipientsAdded: number
) {
    const composeOperation: ComposeOperation = viewState.operation;

    return (
        parentMessageHasAttachments &&
        numberOfDistinctRecipientsAdded > 0 &&
        (composeOperation === ComposeOperation.Reply ||
            composeOperation === ComposeOperation.ReplyAll ||
            composeOperation === ComposeOperation.EditDraft)
    );
}

// Exported for testing purposes only
export function shouldRemoveMostRecentAttachmentsInfoBarMessage(
    viewState: ComposeViewState,
    numberOfDistinctRecipientsAdded: number
) {
    return (
        isIncludeMostRecentAttachmentsInfoBarPresent(viewState) &&
        numberOfDistinctRecipientsAdded === 0
    );
}

function hasUserActedOnIncludeMostRecentAttachmentsMessage(composeId: string) {
    return composeIdsWithActedOnMostRecentAttachmentMessage.indexOf(composeId) !== -1;
}

// Exported for testing reason only
export const updateActedComposeHandler = (composeId: string) => () => {
    composeIdsWithActedOnMostRecentAttachmentMessage.push(composeId);
};

function isIncludeMostRecentAttachmentsInfoBarPresent(viewState: ComposeViewState) {
    const infoBarIds: string[] = infoBarGetIdsHandler(viewState)();
    return infoBarIds.indexOf(INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID) !== -1;
}

function logShowIncludeMostRecentAttachmentsMessageDatapoint(viewState: ComposeViewState) {
    if (!isIncludeMostRecentAttachmentsInfoBarPresent(viewState)) {
        logUsage('AttachmentShowIncludeMostRecentAttachmentsMessage');
    }
}
